/** The web twin of Quantis-Field's WorkerRecordService: the signed-in user's
 *  OWN row from the workforce registers, read directly under RLS.
 *
 *  The registers carry dedicated self-read policies (employees_select_own /
 *  subcontractors_select_own, `user_id = auth.uid()`, verified on the live
 *  database), so this is the same access path the Field app ships with — no
 *  edge route, and the admin-gated register routes are untouched.
 *
 *  Resolution mirrors Field exactly: employees by user_id first and it WINS if
 *  both rows exist, else subcontractors, else none. Failures resolve to none
 *  silently — a profile page must degrade to "no work record", never surface a
 *  pay-data error payload.
 *
 *  Deliberately NOT routed through DataService: the web caches the whole DB in
 *  localStorage (contract_group_database), and pay rates and bank account
 *  numbers must never land in that blob. The background warm and Profile share
 *  one in-memory answer, never stored on the device, refetched per mount. */
import { useEffect, useState } from 'react';
import { getSupabaseClient } from '../../utils/supabase/client';
import { useAuth } from '../../platform/auth/AuthContext';
import { currentBackgroundReadScope, peekBackgroundRead, readInBackground } from '../../services/BackgroundReadCache';

export interface EmployeeRow {
  employmentType?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  startDate?: string | null;
  status?: string | null;
  payType?: string | null;
  payRate?: number | null;
  payCurrency?: string | null;
  pensionScheme?: string | null;
  pensionOptOut?: boolean | null;
  bankAccountName?: string | null;
  bankSortCode?: string | null;
  bankAccountNumber?: string | null;
  cscsNumber?: string | null;
  cscsExpiry?: string | null;
  rightToWorkConfirmed?: boolean | null;
}

export interface SubcontractorRow {
  businessType?: string | null;
  trade?: string | null;
  utr?: string | null;
  crn?: string | null;
  vatRegistered?: boolean | null;
  vatNumber?: string | null;
  verificationStatus?: string | null;
  deductionStatus?: string | null;
  deductionRate?: number | null;
  status?: string | null;
  bankAccountName?: string | null;
  bankSortCode?: string | null;
  bankAccountNumber?: string | null;
  cscsNumber?: string | null;
  cscsExpiry?: string | null;
  rightToWorkConfirmed?: boolean | null;
}

export type WorkerRecord =
  | { kind: 'employee'; row: EmployeeRow }
  | { kind: 'subcontractor'; row: SubcontractorRow }
  | { kind: 'none' };

// Slim SELECTs on purpose (the do-not-show-more rule): ni_number, tax_code,
// date_of_birth, address, end_date and notes exist on the tables but are never
// rendered by Field's Account groups, so they are not fetched at all.
const EMPLOYEE_COLS =
  'employment_type, job_title, department, start_date, status, pay_type, pay_rate, pay_currency, pension_scheme, pension_opt_out, bank_account_name, bank_sort_code, bank_account_number, cscs_number, cscs_expiry, right_to_work_confirmed';
const SUBCONTRACTOR_COLS =
  'business_type, trade, utr, crn, vat_registered, vat_number, verification_status, deduction_status, deduction_rate, status, bank_account_name, bank_sort_code, bank_account_number, cscs_number, cscs_expiry, right_to_work_confirmed';

const mapEmployee = (r: any): EmployeeRow => ({
  employmentType: r.employment_type,
  jobTitle: r.job_title,
  department: r.department,
  startDate: r.start_date,
  status: r.status,
  payType: r.pay_type,
  payRate: r.pay_rate,
  payCurrency: r.pay_currency,
  pensionScheme: r.pension_scheme,
  pensionOptOut: r.pension_opt_out,
  bankAccountName: r.bank_account_name,
  bankSortCode: r.bank_sort_code,
  bankAccountNumber: r.bank_account_number,
  cscsNumber: r.cscs_number,
  cscsExpiry: r.cscs_expiry,
  rightToWorkConfirmed: r.right_to_work_confirmed,
});

const mapSubcontractor = (r: any): SubcontractorRow => ({
  businessType: r.business_type,
  trade: r.trade,
  utr: r.utr,
  crn: r.crn,
  vatRegistered: r.vat_registered,
  vatNumber: r.vat_number,
  verificationStatus: r.verification_status,
  deductionStatus: r.deduction_status,
  deductionRate: r.deduction_rate,
  status: r.status,
  bankAccountName: r.bank_account_name,
  bankSortCode: r.bank_sort_code,
  bankAccountNumber: r.bank_account_number,
  cscsNumber: r.cscs_number,
  cscsExpiry: r.cscs_expiry,
  rightToWorkConfirmed: r.right_to_work_confirmed,
});

const NONE: WorkerRecord = { kind: 'none' };

/** Named once for Profile and the background warm. Kept in memory only
 *  (`persist: false`), and refused if the session belongs to another actor. */
export const workerRecordRead = (actorId?: string) => ({
  name: 'profile/worker-record',
  load: async (): Promise<WorkerRecord> => {
    // Untyped client: the generated database types do not cover the
    // register tables (same precedent as SubscriptionStateService).
    const client = getSupabaseClient() as any;
    const { data: sessionData } = await client.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return NONE;
    if (actorId && userId !== actorId) throw new Error('The signed-in account changed.');

    // Fixed messages: an error payload here can embed row contents, and this is pay and bank data.
    const { data: employees, error } = await client
      .from('employees')
      .select(EMPLOYEE_COLS)
      .eq('user_id', userId)
      .limit(1);
    if (error) throw new Error('The work record could not be read.');
    if (employees?.length) return { kind: 'employee', row: mapEmployee(employees[0]) };

    const { data: subbies, error: subcontractorError } = await client
      .from('subcontractors')
      .select(SUBCONTRACTOR_COLS)
      .eq('user_id', userId)
      .limit(1);
    if (subcontractorError) throw new Error('The work record could not be read.');
    return subbies?.length ? { kind: 'subcontractor', row: mapSubcontractor(subbies[0]) } : NONE;
  },
});

function warmedRecord(workspaceId: string | null | undefined): WorkerRecord | undefined {
  return peekBackgroundRead<WorkerRecord>(currentBackgroundReadScope(workspaceId), workerRecordRead().name)?.data;
}

export function useWorkerRecord(): WorkerRecord {
  const { activeWorkspaceId } = useAuth();
  const [record, setRecord] = useState<WorkerRecord>(() => warmedRecord(activeWorkspaceId) ?? NONE);

  useEffect(() => {
    let cancelled = false;
    // The record is the person's, not the workspace's, so a switch keeps it on screen while it refreshes.
    const scope = currentBackgroundReadScope(activeWorkspaceId);
    const read = workerRecordRead(scope?.actorId);
    (scope ? readInBackground(scope, read.name, read.load, { persist: false }) : read.load())
      .then((next) => { if (!cancelled) setRecord(next); })
      // Fail soft to 'none' — same as Field. No logging, for the same reason as above.
      .catch(() => { if (!cancelled) setRecord(NONE); });
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);

  return record;
}
